import { Component, type ReactNode } from 'react'
import { LoggingGateway } from '@core/services/logging.gateway'
import { ErrorReportGateway } from '@core/services/error-report.gateway'

type Props = { children: ReactNode }
type State = { error: Error | null }

/**
 * Crash boundary — tier 3 of the error-handling convention. Tier 1 (gateway
 * Result failures) and tier 2 (expected presenter-level failures, surfaced
 * via a Repository's own load state/FeedbackPresenter) should never reach
 * here; this only catches genuinely unexpected render-time errors — bugs,
 * not "the API said no".
 *
 * Deliberately dependency-free: no DI, no gateway for the reload button, and
 * `new LoggingGateway()` directly instead of injecting it — LoggingGateway
 * takes no dependencies of its own, so this still logs even if the container
 * itself is what's broken, without leaning on any of the app's normal
 * DI-mediated conventions.
 *
 * That is also why this is the one component in the app allowed to hold copy.
 * Every other string comes from a Presenter via TranslationsConfig, but
 * resolving one here would mean asking the container for I18nRepository at
 * exactly the moment the container may be the thing that failed. Untranslated
 * English is a better outcome than a blank screen.
 */
const logging = new LoggingGateway()
const errorReport = new ErrorReportGateway()

export class ErrorBoundaryComponent extends Component<Props, State> {
  state: State = { error: null }

  static getDerivedStateFromError(error: Error): State {
    return { error }
  }

  componentDidCatch(error: Error) {
    logging.error('Unhandled render error', { message: error.message, stack: error.stack })
    errorReport.report(`Unhandled render error: ${error.message}`, error.stack)
  }

  render() {
    if (this.state.error) {
      return (
        <div className="flex min-h-screen flex-col items-center justify-center gap-y-4 p-4 text-center">
          <p className="text-lg font-semibold text-shell-ink">Something went wrong.</p>
          <p className="text-sm text-shell-ink-secondary">Please reload and try again.</p>
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="rounded-md bg-shell-accent px-4 py-2 text-sm font-semibold text-white hover:opacity-90"
          >
            Reload
          </button>
        </div>
      )
    }

    return this.props.children
  }
}
